import { gql, useMutation } from "@apollo/client";
import { client } from "../apolloClient";

/**
 * useResourceApi custom hook for resource operations.
 * - Provides CRUD operations for resources using Apollo Client and GraphQL.
 * - Handles cache updates for resource queries and mutations.
 * - Exposes helpers for reading/writing/updating resources and departments in Apollo cache.
 * - Returns methods for fetching, creating, updating, and deleting resources.
 * - Supports fetching by role, role category, and department.
 * - Includes cache helpers for resources and departments.
 * - Handles optimistic UI updates and error logging.
 */

// GraphQL Queries and Mutations
const RESOURCE_QUERIES = {
  GET_RESOURCES: gql`
    query GetResources {
      GetResources {
        resource_id
        username
        email
        mobile
        designation
        first_name
        last_name
        previous_year_of_experience
        date_of_joining
        type_of_service
        business_unit
        reporting_manager
        availability
        country_id
        employee_code
        department
        is_active
        created_on
        updated_on
        role_id
      }
    }
  `,

  // Query to fetch all departments
  GET_DEPARTMENTS: gql`
    query Query {
      GetDepartments
    }
  `,

  // Query to check if a resource has any reporting hierarchy
  HAS_REPORTING_HIERARCHY: gql`
    query HasReportingHierarchy($resource_id: ID!) {
      HasReportingHierarchy(resource_id: $resource_id)
    }
  `,

  // Query to fetch all resources under a reporting manager (recursive)
  GET_RESOURCES_BY_REPORTING_MANAGER_ID: gql`
    query GetResourcesByReportingManagerId($resource_id: ID!) {
      GetResourcesByReportingManagerId(resource_id: $resource_id) {
        resource_id
        username
        email
        first_name
        last_name
        designation
        reporting_manager
        department
        employee_code
      }
    }
  `,

  // Mutation to add a new resource
  ADD_RESOURCE: gql`
    mutation AddResource(
      $username: String!
      $email: String!
      $mobile: String
      $designation: String!
      $firstName: String!
      $lastName: String!
      $previousYearOfExperience: Float
      $dateOfJoining: String!
      $typeOfService: TypeService!
      $businessUnit: Int!
      $reportingManager: Int!
      $availability: String!
      $countryId: Int!
      $employeeCode: String!
      $isActive: Boolean!
    ) {
      AddResource(
        username: $username
        email: $email
        mobile: $mobile
        designation: $designation
        first_name: $firstName
        last_name: $lastName
        previous_year_of_experience: $previousYearOfExperience
        date_of_joining: $dateOfJoining
        type_of_service: $typeOfService
        business_unit: $businessUnit
        reporting_manager: $reportingManager
        availability: $availability
        country_id: $countryId
        employee_code: $employeeCode
        is_active: $isActive
      ) {
        resource_id
        username
        email
        mobile
        designation
        first_name
        last_name
        previous_year_of_experience
        date_of_joining
        type_of_service
        business_unit
        reporting_manager
        availability
        country_id
        employee_code
        department
        is_active
        created_on
        updated_on
      }
    }
  `,

  // Mutation to update an existing resource
  UPDATE_RESOURCE: gql`
    mutation UpdateResource(
      $resource_id: ID!
      $username: String!
      $email: String!
      $mobile: String
      $designation: String!
      $first_name: String!
      $last_name: String!
      $previous_year_of_experience: Float
      $date_of_joining: String!
      $type_of_service: TypeService!
      $business_unit: Int!
      $reporting_manager: Int!
      $availability: String!
      $country_id: Int!
      $employee_code: String!
      $is_active: Boolean!
      $role_id: Int!
    ) {
      UpdateResource(
        resource_id: $resource_id
        username: $username
        email: $email
        mobile: $mobile
        designation: $designation
        first_name: $first_name
        last_name: $last_name
        previous_year_of_experience: $previous_year_of_experience
        date_of_joining: $date_of_joining
        type_of_service: $type_of_service
        business_unit: $business_unit
        reporting_manager: $reporting_manager
        availability: $availability
        country_id: $country_id
        employee_code: $employee_code
        is_active: $is_active
        role_id: $role_id
      ) {
        resource_id
        username
        email
        mobile
        designation
        first_name
        last_name
        previous_year_of_experience
        date_of_joining
        type_of_service
        business_unit
        reporting_manager
        availability
        country_id
        employee_code
        department
        is_active
        created_on
        updated_on
        role_id
      }
    }
  `,

  // Mutation to delete a resource
  DELETE_RESOURCE: gql`
    mutation DeleteResource($resource_id: ID!) {
      DeleteResource(resource_id: $resource_id) {
        resource_id
      }
    }
  `,

  // Query to fetch resources by role IDs
  GET_RESOURCES_BY_ROLE_IDS: gql`
    query GetResourceByRoleIds($roleIds: [String]) {
      GetResourceByRoleIds(role_ids: $roleIds) {
        resource_id
        username
        email
        mobile
        designation
        first_name
        last_name
        previous_year_of_experience
        date_of_joining
        type_of_service
        reporting_manager
        availability
        country_id
        employee_code
        department
        is_active
        business_unit
        created_on
        updated_on
        role_id
      }
    }
  `,

  // Query to fetch resources by role category ID
  GET_RESOURCES_BY_ROLE_CATEGORY_ID: gql`
    query GetResourceByRoleCategoryId($roleCategoryId: ID!) {
      GetResourceByRoleCategoryId(role_category_id: $roleCategoryId) {
        resource_id
        username
        email
        mobile
        designation
        first_name
        last_name
        previous_year_of_experience
        date_of_joining
        type_of_service
        business_unit
        reporting_manager
        availability
        country_id
        employee_code
        department
        is_active
        created_on
        updated_on
        role_id
      }
    }
  `,
};

// Cache operations for resources and departments
const cacheOperations = {
  /**
   * Reads all resources from Apollo cache.
   * @returns {Array} Array of resource objects.
   */
  readResources: () => {
    try {
      const { GetResources = [] } =
        client.readQuery({ query: RESOURCE_QUERIES.GET_RESOURCES }) || {};
      return GetResources;
    } catch {
      return [];
    }
  },
  /**
   * Reads resources by role IDs from Apollo cache.
   * @param {Array} roleIds
   * @returns {Array}
   */
  readResourcesByRoleIds: (roleIds) => {
    try {
      const { GetResourceByRoleIds = [] } =
        client.readQuery({
          query: RESOURCE_QUERIES.GET_RESOURCES_BY_ROLE_IDS,
          variables: { roleIds: roleIds },
        }) || {};
      return GetResourceByRoleIds;
    } catch {
      return [];
    }
  },
  /**
   * Writes resources to Apollo cache.
   * @param {Array} resources
   */
  writeResources: (resources) => {
    client.writeQuery({
      query: RESOURCE_QUERIES.GET_RESOURCES,
      data: { GetResources: resources },
    });
  },
  /**
   * Reads departments from Apollo cache.
   * @returns {Array}
   */
  readDepartments: () => {
    try {
      const { GetDepartments = [] } =
        client.readQuery({ query: RESOURCE_QUERIES.GET_DEPARTMENTS }) || {};
      return GetDepartments;
    } catch {
      return [];
    }
  },
  /**
   * Writes departments to Apollo cache.
   * @param {Array} departments
   */
  writeDepartments: (departments) => {
    client.writeQuery({
      query: RESOURCE_QUERIES.GET_DEPARTMENTS,
      data: { GetDepartments: departments },
    });
  },
};

export const useResourceApi = () => {
  // Add resource mutation with cache update
  const [addResourceMutation] = useMutation(RESOURCE_QUERIES.ADD_RESOURCE, {
    update(cache, { data: { AddResource } }) {
      try {
        const existingData = cache.readQuery({
          query: RESOURCE_QUERIES.GET_RESOURCES,
        });
        if (existingData) {
          cache.writeQuery({
            query: RESOURCE_QUERIES.GET_RESOURCES,
            data: {
              GetResources: [AddResource, ...existingData.GetResources],
            },
          });
        }
      } catch (error) {
        console.error("Cache update error:", error);
      }
    },
  });

  // Update resource mutation with cache update
  const [updateResourceMutation] = useMutation(
    RESOURCE_QUERIES.UPDATE_RESOURCE,
    {
      update(cache, { data: { UpdateResource } }) {
        try {
          const existingData = cache.readQuery({
            query: RESOURCE_QUERIES.GET_RESOURCES,
          });
          if (existingData) {
            const newData = existingData.GetResources.map((resource) =>
              resource.resource_id === UpdateResource.resource_id
                ? UpdateResource
                : resource
            );
            cache.writeQuery({
              query: RESOURCE_QUERIES.GET_RESOURCES,
              data: { GetResources: newData },
            });
          }
        } catch (error) {
          console.error("Error updating cache after updating resource:", error);
        }
      },
    }
  );

  // Delete resource mutation with cache update
  const [deleteResourceMutation] = useMutation(
    RESOURCE_QUERIES.DELETE_RESOURCE,
    {
      update(cache, { data: { DeleteResource } }) {
        try {
          const existingData = cache.readQuery({
            query: RESOURCE_QUERIES.GET_RESOURCES,
          });
          if (existingData) {
            cache.writeQuery({
              query: RESOURCE_QUERIES.GET_RESOURCES,
              data: {
                GetResources: existingData.GetResources.filter(
                  (resource) =>
                    resource.resource_id !== DeleteResource.resource_id
                ),
              },
            });
          }
        } catch (error) {
          console.error("Error updating cache after deleting resource:", error);
        }
      },
    }
  );

  /**
   * Fetches all resources from the backend.
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const getResources = async () => {
    try {
      const { data } = await client.query({
        query: RESOURCE_QUERIES.GET_RESOURCES,
        fetchPolicy: "network-only",
        nextFetchPolicy: "cache-first",
      });

      if (!data?.GetResources) {
        throw new Error("No Resources data received");
      }

      return { success: true, data: data.GetResources };
    } catch (error) {
      console.error("Resource fetch error:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Fetches all departments from the backend.
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const getDepartments = async () => {
    try {
      const { data } = await client.query({
        query: RESOURCE_QUERIES.GET_DEPARTMENTS,
        fetchPolicy: "network-only",
        nextFetchPolicy: "cache-first",
      });

      if (!data?.GetDepartments) {
        throw new Error("No Departments data received");
      }

      return { success: true, data: data.GetDepartments };
    } catch (error) {
      console.error("Departments fetch error:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Fetches resources by role IDs.
   * @param {Array<string|number>} roleIds
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const getResourcesByRoleIds = async (roleIds) => {
    try {
      const { data } = await client.query({
        query: RESOURCE_QUERIES.GET_RESOURCES_BY_ROLE_IDS,
        variables: { roleIds: roleIds },
        fetchPolicy: "network-only",
        nextFetchPolicy: "cache-first",
      });

      if (!data?.GetResourceByRoleIds) {
        throw new Error("No Resources data received");
      }

      return { success: true, data: data.GetResourceByRoleIds };
    } catch (error) {
      console.error("Resource fetch error:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Fetches resources by role category ID.
   * @param {string|number} roleCategoryId
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const getResourcesByRoleCategoryId = async (roleCategoryId) => {
    try {
      const { data } = await client.query({
        query: RESOURCE_QUERIES.GET_RESOURCES_BY_ROLE_CATEGORY_ID,
        variables: { roleCategoryId: roleCategoryId.toString() },
        fetchPolicy: "network-only",
        nextFetchPolicy: "cache-first",
      });

      if (!data?.GetResourceByRoleCategoryId) {
        throw new Error("No Resources data received");
      }

      return { success: true, data: data.GetResourceByRoleCategoryId };
    } catch (error) {
      console.error("Resource fetch error:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Creates a new resource.
   * @param {Object} formData
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const createResource = async (formData) => {
    try {
      const { data } = await addResourceMutation({
        variables: {
          username: formData.username,
          email: formData.email,
          mobile: formData.mobile,
          designation: formData.designation,
          firstName: formData.first_name,
          lastName: formData.last_name,
          previousYearOfExperience: parseFloat(
            formData.previous_year_of_experience
          ),
          dateOfJoining: formData.date_of_joining,
          typeOfService: formData.type_of_service,
          businessUnit: parseInt(formData.business_unit),
          reportingManager: parseInt(formData.reporting_manager),
          availability: formData.availability,
          countryId: parseInt(formData.country_id),
          employeeCode: formData.employee_code,
          isActive: formData.is_active,
        },
      });

      if (data?.AddResource) {
        return { success: true, data: data.AddResource };
      } else {
        throw new Error("Failed to create resource");
      }
    } catch (error) {
      console.error("Error creating resource:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Updates an existing resource.
   * @param {string|number} resourceId
   * @param {Object} formData
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const updateResource = async (resourceId, formData) => {
    try {
      const { data } = await updateResourceMutation({
        variables: {
          resource_id: resourceId.toString(),
          username: formData?.username,
          email: formData.email,
          mobile: formData.mobile,
          designation: formData.designation,
          first_name: formData.first_name,
          last_name: formData.last_name,
          previous_year_of_experience: parseFloat(
            formData.previous_year_of_experience
          ),
          date_of_joining: formData.date_of_joining,
          type_of_service: formData.type_of_service,
          business_unit: parseInt(formData.business_unit),
          reporting_manager: parseInt(formData.reporting_manager),
          availability: formData.availability,
          country_id: parseInt(formData.country_id),
          employee_code: formData.employee_code,
          is_active: formData.is_active,
          role_id: formData.role_id
            ? parseInt(formData.role_id)
            : formData.role_id,
        },
      });

      if (data?.UpdateResource) {
        return { success: true, data: data.UpdateResource };
      } else {
        throw new Error("Failed to update resource");
      }
    } catch (error) {
      console.error("Error updating resource:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Deletes a resource by its ID.
   * @param {string|number} resourceId
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const deleteResource = async (resourceId) => {
    try {
      const { data } = await deleteResourceMutation({
        variables: { resource_id: resourceId.toString() },
      });

      return { success: true, data: data.DeleteResource };
    } catch (error) {
      console.error("Error deleting resource:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Checks whether the given resource has any reporting hierarchy.
   * @param {string|number} resourceId
   * @returns {Promise<{success: boolean, data?: boolean, error?: string}>}
   */
  const hasReportingHierarchy = async (resourceId) => {
    try {
      const { data } = await client.query({
        query: RESOURCE_QUERIES.HAS_REPORTING_HIERARCHY,
        variables: { resource_id: resourceId.toString() },
        fetchPolicy: "network-only",
      });
      return {
        success: true,
        data: Boolean(data?.HasReportingHierarchy),
      };
    } catch (error) {
      console.error("Error checking reporting hierarchy:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Fetches all resources under the given reporting manager (direct + indirect).
   * @param {string|number} resourceId
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const getResourcesByReportingManagerId = async (resourceId) => {
    try {
      const { data } = await client.query({
        query: RESOURCE_QUERIES.GET_RESOURCES_BY_REPORTING_MANAGER_ID,
        variables: { resource_id: resourceId.toString() },
        fetchPolicy: "network-only",
      });
      return {
        success: true,
        data: data?.GetResourcesByReportingManagerId || [],
      };
    } catch (error) {
      console.error("Error fetching reporting hierarchy resources:", error);
      return { success: false, error: error.message };
    }
  };

  return {
    getResources,
    createResource,
    updateResource,
    deleteResource,
    getResourcesByRoleIds,
    getResourcesByRoleCategoryId,
    getDepartments,
    hasReportingHierarchy,
    getResourcesByReportingManagerId,
    cacheOperations,
  };
};
